import React, { useEffect, useState } from "react";
import { createPodInstance } from "@/services/api/agentic";
import type { PodInstance } from "@/services/api/agentic/types";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { useNavigate } from "react-router-dom";
export interface CreatePodProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  routeTemplateId?: string;
  instanceName: string;
}

// Helper function to dispatch Instance updates (same as in ProjectsList)
function dispatchProjectUpdate(type: "add" | "update" | "delete", data: any) {
  const event = new CustomEvent("projectUpdate", {
    detail:
      typeof data === "string" ? { type, podId: data } : { type, pod: data },
  });
  window.dispatchEvent(event);
}

export function CreatePodInstance({
  isOpen,
  onClose,
  onSuccess,
  routeTemplateId,
  instanceName,
}: CreatePodProps) {
  const [projectName, setProjectName] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const { user } = useAuth();
  const [clientId, setClientId] = useState<string | null>(null);

  // Reset form when dialog opens and fetch initial data
  useEffect(() => {
    if (isOpen) {
      // Initial resets
      setProjectName("");
      setSubmitError(null);

      if (user?.company_id) {
        setClientId(user.company_id);
      } else {
        console.warn(
          "User company_id not available for clientId on dialog open."
        );
      }
      if (!user?.id) {
        console.warn("User ID not available on dialog open.");
      }

      const initialize = async () => {
        setSubmitError(null);

        if (!routeTemplateId) {
          setSubmitError(
            `Template ID is missing. Cannot create ${instanceName.toLowerCase()}.`
          );

          setSelectedTemplateId(null);
          return;
        }
        setSelectedTemplateId(routeTemplateId);
      };

      initialize();
    }
  }, [isOpen, user, routeTemplateId]);

  const handleCreatePodInstance = async () => {
    if (!clientId) {
      setSubmitError("Client ID is not set. Cannot create instance.");
      return;
    }
    if (!selectedTemplateId) {
      setSubmitError(
        `A valid template is not selected. Cannot create ${instanceName.toLowerCase()}. This might be due to an invalid or missing template ID in the route.`
      );
      return;
    }
    if (!projectName.trim()) {
      setSubmitError(`${instanceName} name is required`);
      return;
    }
    if (!user?.id) {
      setSubmitError("User information is not available. Cannot set owner.");
      return;
    }

    try {
      setIsCreating(true);
      setSubmitError(null);
      const payload: Omit<PodInstance, "id" | "created_at" | "updated_at"> = {
        template_id: selectedTemplateId,
        name: projectName,
        team_ids: [],
        client_id: clientId,
        owner_id: user.id,
        state: { current_status: "", current_stage: "", call_to_action: "" },
        rules: [],
        tasks: [],
        data: {},
        log: [
          {
            timestamp: new Date().toISOString(),
            message: "Pod instance created.",
          },
        ],
        variables: {},
        metadata: {},
      };

      const response = await createPodInstance(payload);
      dispatchProjectUpdate("add", response);
      if (onSuccess) onSuccess();
      navigate(
        `/pod/${selectedTemplateId}/${
          (response as PodInstance & { _id: string })._id
        }`
      );
      onClose(); // Close dialog on success
    } catch (apiError: any) {
      if (apiError.response?.data?.detail) {
        setSubmitError(`API Error: ${apiError.response.data.detail}`);
      } else {
        setSubmitError(
          apiError instanceof Error
            ? apiError.message
            : "Failed to create pod instance"
        );
      }
    } finally {
      setIsCreating(false);
    }
  };

  const capitalizeFirstLetter = (string: string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const renderFormContent = () => (
    <>
      <div className="mb-4">
        <label
          htmlFor="instance-name"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {capitalizeFirstLetter(instanceName)} name{" "}
          <span className="text-slate-400">*</span>
        </label>
        <input
          id="instance-name"
          type="text"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder-gray-400"
          placeholder={`Enter ${instanceName.toLowerCase()} name`}
          required
        />
      </div>
    </>
  );

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} size="2xl">
        <DialogTitle>Create {capitalizeFirstLetter(instanceName)}</DialogTitle>

        <DialogBody>
          <div className="space-y-6">
            {submitError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30 text-sm">
                {submitError}
              </div>
            )}

            {!submitError && selectedTemplateId && renderFormContent()}
            {!submitError && !selectedTemplateId && !routeTemplateId && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No template ID provided. Cannot create{" "}
                {instanceName.toLowerCase()}.
              </p>
            )}
            {!submitError && !selectedTemplateId && routeTemplateId && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The specified template could not be loaded. Please verify the
                template ID.
              </p>
            )}
          </div>
        </DialogBody>

        <DialogActions>
          <>
            <Button onClick={onClose}>Cancel</Button>

            <Button
              onClick={handleCreatePodInstance}
              disabled={
                !projectName ||
                !selectedTemplateId ||
                !user?.id ||
                !!submitError ||
                isCreating
              }
              color="indigo"
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </>
        </DialogActions>
      </Dialog>
    </>
  );
}
